import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import styles from './css/Contact.module.css';

const fadeUp = {
  hidden:  { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 },
};

const SERVICES = [
  'Web Development',
  'Mobile App Development',
  'Full-Stack Solutions',
  'Software Integration',
  'API Development',
  'Consulting',
  'Other',
];

const INITIAL = {
  firstName: '',
  lastName: '',
  email: '',
  company: '',
  service: '',
  budget: '',
  message: '',
};

function Contact() {
  const [form, setForm]               = useState(INITIAL);
  const [sending, setSending]         = useState(false);
  const [submitted, setSubmitted]     = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const [errors, setErrors]           = useState({});
  const [honeypot, setHoneypot]       = useState('');
  const [captchaInput, setCaptchaInput] = useState('');
  const [captcha, setCaptcha]         = useState({ q: '', answer: 0 });
  const captchaAnswerRef              = useRef(0);
  const captchaInputRef               = useRef('');
  const [formStartTime]               = useState(() => Date.now());

  const newCaptcha = () => {
    const a = Math.floor(Math.random() * 12) + 1;
    const b = Math.floor(Math.random() * 12) + 1;
    captchaAnswerRef.current = a + b;
    setCaptcha({ q: `${a} + ${b}`, answer: a + b });
  };

  useEffect(() => { newCaptcha(); }, []);

  const validate = () => {
    const e = {};
    if (!form.firstName.trim()) e.firstName = 'Required';
    if (!form.lastName.trim())  e.lastName  = 'Required';
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      e.email = 'Valid email required';
    if (!form.message.trim())   e.message   = 'Please enter a message';
    if (parseInt(captchaInputRef.current, 10) !== captchaAnswerRef.current)
      e.captcha = 'Incorrect — please try again';
    return e;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: undefined }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Layer 1 — Honeypot: real users never fill this field
    if (honeypot) return;
    // Layer 2 — Time check: bots submit in milliseconds
    if (Date.now() - formStartTime < 3000) return;
    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      // Regenerate captcha on wrong answer
      if (errs.captcha) { newCaptcha(); captchaInputRef.current = ''; setCaptchaInput(''); }
      return;
    }
    setSending(true);
    setSubmitError(false);
    try {
      const res = await fetch('https://byte79.com/contact/contact.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const result = await res.json();
      if (result.success) {
        setSubmitted(true);
      } else {
        setSubmitError(true);
      }
    } catch {
      setSubmitError(true);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className={styles.page}>

      {/* Header */}
      <motion.div
        className={styles.pageHeader}
        initial="hidden"
        animate="visible"
        variants={fadeUp}
        transition={{ duration: 0.7 }}
      >
        <span className={styles.sectionLabel}>Get In Touch</span>
        <div className={styles.divider} />
        <h1 className={styles.pageTitle}>Let's Build Something Great</h1>
        <p className={styles.pageSubtitle}>
          Whether you have a fully scoped project or just an idea — reach out and we'll work through it together.
          Typically respond within one business day.
        </p>
      </motion.div>

      <div className={styles.layout}>

        {/* ── Info column ── */}
        <motion.div
          className={styles.infoCol}
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          transition={{ duration: 0.7, delay: 0.1 }}
        >
          <div className={styles.availBadge}>
            <span className={styles.availDot} />
            Available for new projects
          </div>

          <div className={styles.infoCard}>
            <span className={styles.infoIcon}>✉️</span>
            <div className={styles.infoCardBody}>
              <p className={styles.infoCardTitle}>Email</p>
              <p className={styles.infoCardText}>
                <a href="mailto:info@byte79.com" className={styles.infoCardLink}>
                  info@byte79.com
                </a>
              </p>
            </div>
          </div>

          <div className={styles.infoCard}>
            <span className={styles.infoIcon}>🌐</span>
            <div className={styles.infoCardBody}>
              <p className={styles.infoCardTitle}>Website</p>
              <p className={styles.infoCardText}>
                <a href="https://byte79.com" target="_blank" rel="noreferrer" className={styles.infoCardLink}>
                  byte79.com
                </a>
              </p>
            </div>
          </div>

          <div className={styles.infoCard}>
            <span className={styles.infoIcon}>🕐</span>
            <div className={styles.infoCardBody}>
              <p className={styles.infoCardTitle}>Response Time</p>
              <p className={styles.infoCardText}>Within 1 business day. For urgent enquiries, email directly.</p>
            </div>
          </div>

          <div className={styles.infoCard}>
            <span className={styles.infoIcon}>🏢</span>
            <div className={styles.infoCardBody}>
              <p className={styles.infoCardTitle}>Company</p>
              <p className={styles.infoCardText}>
                Byte79 Ltd<br />
                Registered in England &amp; Wales<br />
                35+ years industry experience
              </p>
            </div>
          </div>
        </motion.div>

        {/* ── Form column ── */}
        <motion.div
          className={styles.formCard}
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          transition={{ duration: 0.7, delay: 0.2 }}
        >
          {submitted ? (
            <div className={styles.successBox}>
              <span className={styles.successIcon}>🎉</span>
              <h2 className={styles.successTitle}>Message received!</h2>
              <p className={styles.successText}>
                Thanks for reaching out, {form.firstName}. We'll be in touch shortly.
              </p>
              <button className={styles.resetBtn} onClick={() => { setSubmitted(false); setSubmitError(false); setForm(INITIAL); }}>
                Send another message
              </button>
            </div>
          ) : (
            <form className={styles.form} onSubmit={handleSubmit} noValidate>

              <div className={styles.formRow}>
                <div className={styles.field}>
                  <label className={styles.label}>First Name *</label>
                  <input
                    className={styles.input}
                    type="text"
                    name="firstName"
                    placeholder="John"
                    value={form.firstName}
                    onChange={handleChange}
                    style={errors.firstName ? { borderColor: '#f87171' } : {}}
                  />
                  {errors.firstName && <span style={{ fontSize: '0.75rem', color: '#f87171' }}>{errors.firstName}</span>}
                </div>
                <div className={styles.field}>
                  <label className={styles.label}>Last Name *</label>
                  <input
                    className={styles.input}
                    type="text"
                    name="lastName"
                    placeholder="Smith"
                    value={form.lastName}
                    onChange={handleChange}
                    style={errors.lastName ? { borderColor: '#f87171' } : {}}
                  />
                  {errors.lastName && <span style={{ fontSize: '0.75rem', color: '#f87171' }}>{errors.lastName}</span>}
                </div>
              </div>

              <div className={styles.formRow}>
                <div className={styles.field}>
                  <label className={styles.label}>Email Address *</label>
                  <input
                    className={styles.input}
                    type="email"
                    name="email"
                    placeholder="you@example.com"
                    value={form.email}
                    onChange={handleChange}
                    style={errors.email ? { borderColor: '#f87171' } : {}}
                  />
                  {errors.email && <span style={{ fontSize: '0.75rem', color: '#f87171' }}>{errors.email}</span>}
                </div>
                <div className={styles.field}>
                  <label className={styles.label}>Company</label>
                  <input
                    className={styles.input}
                    type="text"
                    name="company"
                    placeholder="Your Company (optional)"
                    value={form.company}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className={styles.formRow}>
                <div className={styles.field}>
                  <label className={styles.label}>Service Required</label>
                  <select
                    className={styles.select}
                    name="service"
                    value={form.service}
                    onChange={handleChange}
                  >
                    <option value="">Select a service…</option>
                    {SERVICES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className={styles.field}>
                  <label className={styles.label}>Approximate Budget</label>
                  <select
                    className={styles.select}
                    name="budget"
                    value={form.budget}
                    onChange={handleChange}
                  >
                    <option value="">Select a range…</option>
                    <option value="under5k">Under £5,000</option>
                    <option value="5to15k">£5,000 – £15,000</option>
                    <option value="15to50k">£15,000 – £50,000</option>
                    <option value="50kplus">£50,000+</option>
                    <option value="discuss">Prefer to discuss</option>
                  </select>
                </div>
              </div>

              <div className={styles.field}>
                <label className={styles.label}>Your Message *</label>
                <textarea
                  className={styles.textarea}
                  name="message"
                  placeholder="Tell us about your project — goals, timelines, any details that would help us understand what you need…"
                  value={form.message}
                  onChange={handleChange}
                  style={errors.message ? { borderColor: '#f87171' } : {}}
                />
                {errors.message && <span style={{ fontSize: '0.75rem', color: '#f87171' }}>{errors.message}</span>}
              </div>

              {/* Honeypot — invisible to humans, bots fill it in */}
              <div className={styles.honeypot} aria-hidden="true">
                <label htmlFor="website">Leave this blank</label>
                <input
                  id="website"
                  name="website"
                  type="text"
                  value={honeypot}
                  onChange={e => setHoneypot(e.target.value)}
                  tabIndex={-1}
                  autoComplete="off"
                />
              </div>

              {/* Math captcha */}
              <div className={styles.field}>
                <label className={styles.label}>Spam check *</label>
                <div className={styles.captchaRow}>
                  <span className={styles.captchaQuestion}>What is {captcha.q}?</span>
                  <input
                    className={`${styles.input} ${styles.captchaInput}`}
                    type="number"
                    placeholder="Answer"
                    value={captchaInput}
                    onChange={e => {
                      captchaInputRef.current = e.target.value;
                      setCaptchaInput(e.target.value);
                      if (errors.captcha) setErrors(prev => ({ ...prev, captcha: undefined }));
                    }}
                    autoComplete="off"
                  />
                </div>
                {errors.captcha && <span style={{ fontSize: '0.75rem', color: '#f87171' }}>{errors.captcha}</span>}
              </div>

              {submitError && (
                <p style={{ color: '#f87171', fontSize: '0.875rem', marginBottom: '0.5rem' }}>
                  Something went wrong. Please try again or email us directly.
                </p>
              )}

              <button className={styles.submitBtn} type="submit" disabled={sending}>
                {sending ? (
                  <>
                    <span style={{ fontSize: '0.9rem' }}>⏳</span> Sending…
                  </>
                ) : (
                  <>Send Message →</>
                )}
              </button>

            </form>
          )}
        </motion.div>

      </div>
    </div>
  );
}

export default Contact;
